import { useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell, LabelList } from "recharts";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { Reveal } from "@/components/Reveal";
import { Seo } from "@/components/Seo";
import { CASE_STUDIES } from "@/data/site";
import { API } from "@/lib/api";
import { ArrowLeft, ArrowRight, Check } from "lucide-react";
import { useRef } from "react";

const HEX = { orange: "#FF7A00", magenta: "#E6117F", indigo: "#4C3DB0" };

function BeforeAfter({ metrics, color }) {
  return (
    <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
      {metrics.map((m) => {
        const data = [
          { name: "Before", value: m.before },
          { name: "After", value: m.after },
        ];
        return (
          <div key={m.label} className="rounded-2xl border border-white/8 bg-surface p-6" data-testid={`chart-${m.label}`}>
            <div className="mb-1 flex items-baseline justify-between">
              <span className="font-display text-sm font-semibold text-white">{m.label}</span>
              <span className="font-mono text-xs text-muted">Before → After</span>
            </div>
            <ResponsiveContainer width="100%" height={150}>
              <BarChart data={data} margin={{ top: 20, right: 6, left: 6, bottom: 0 }}>
                <XAxis dataKey="name" tick={{ fill: "#8A84A4", fontSize: 12 }} axisLine={false} tickLine={false} />
                <YAxis hide domain={[0, "dataMax"]} />
                <Tooltip cursor={{ fill: "rgba(255,255,255,0.04)" }} contentStyle={{ background: "#161224", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8, color: "#fff" }} formatter={(v) => [`${v}${m.suffix}`, m.label]} />
                <Bar dataKey="value" radius={[6, 6, 0, 0]} maxBarSize={70}>
                  <Cell fill="rgba(255,255,255,0.16)" />
                  <Cell fill={color} />
                  <LabelList dataKey="value" position="top" formatter={(v) => `${v}${m.suffix}`} fill="#fff" fontSize={12} fontWeight={700} />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        );
      })}
    </div>
  );
}

function BarsChart({ chart, color }) {
  return (
    <div className="rounded-2xl border border-white/8 bg-surface p-6">
      {chart.highlight && (
        <div className="mb-4 flex items-baseline gap-3">
          <span className="font-display text-5xl font-black" style={{ color }}>{chart.highlight.value}</span>
          <span className="text-sm text-muted">{chart.highlight.label}</span>
        </div>
      )}
      <ResponsiveContainer width="100%" height={280}>
        <BarChart data={chart.metrics} margin={{ top: 24, right: 10, left: 0, bottom: 0 }}>
          <XAxis dataKey="label" tick={{ fill: "#8A84A4", fontSize: 12 }} axisLine={false} tickLine={false} interval={0} />
          <YAxis hide domain={[0, "dataMax"]} />
          <Tooltip cursor={{ fill: "rgba(255,255,255,0.04)" }} contentStyle={{ background: "#161224", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8, color: "#fff" }} formatter={(v) => [`${v}${chart.unit || ""}`, ""]} />
          <Bar dataKey="value" radius={[6, 6, 0, 0]} maxBarSize={90}>
            {chart.metrics.map((_, i) => <Cell key={i} fill={color} fillOpacity={0.55 + (i / chart.metrics.length) * 0.45} />)}
            <LabelList dataKey="value" position="top" formatter={(v) => `${v}${chart.unit || ""}`} fill="#fff" fontSize={13} fontWeight={700} />
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

export default function CaseStudyPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const cs = CASE_STUDIES.find((c) => c.id === id);
  const others = CASE_STUDIES.filter((c) => c.id !== id);
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const yImg = useTransform(scrollYProgress, [0, 1], [0, 140]);
  const scaleImg = useTransform(scrollYProgress, [0, 1], [1, 1.15]);

  useEffect(() => { window.scrollTo(0, 0); }, [id]);

  if (!cs) {
    return (
      <div className="App bg-ink">
        <Header />
        <div className="flex min-h-[60vh] flex-col items-center justify-center pt-32 text-center">
          <h1 className="font-display text-3xl font-bold">Case study not found</h1>
          <Link to="/" className="mt-4 text-orange">Back home</Link>
        </div>
        <Footer />
      </div>
    );
  }

  const color = HEX[cs.accent] || HEX.orange;

  return (
    <div className="App bg-ink" data-testid="case-study-page">
      <Seo
        title={`${cs.client} — Amazon Ads Case Study | DNA eCommerce Ads`}
        description={cs.summary}
        image={`${API}/og/case-study/${cs.id}.png`}
        type="article"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Article",
          headline: `${cs.client} — Amazon Ads Case Study`,
          description: cs.summary,
          image: [`${API}/og/case-study/${cs.id}.png`, cs.image].filter(Boolean),
          about: cs.client,
          author: { "@type": "Organization", name: "DNA eCommerce Ads" },
          publisher: {
            "@type": "Organization",
            name: "DNA eCommerce Ads",
            logo: { "@type": "ImageObject", url: `${window.location.origin}/dna-logo.png` },
          },
          mainEntityOfPage: window.location.href,
        }}
      />
      <Header />

      {/* Hero */}
      <section ref={heroRef} className="relative h-[85vh] min-h-[560px] overflow-hidden">
        <motion.div style={{ y: yImg, scale: scaleImg }} className="absolute inset-0">
          <img src={cs.image} alt={cs.client} className="h-full w-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/70 to-ink/40" />
        </motion.div>
        <div className="relative mx-auto flex h-full max-w-7xl flex-col justify-end px-5 pb-16 lg:px-8">
          <button onClick={() => navigate(-1)} className="mb-8 inline-flex w-fit items-center gap-2 text-sm text-white/70 hover:text-white" data-testid="cs-back">
            <ArrowLeft className="h-4 w-4" /> Back
          </button>
          <span className="font-mono text-xs uppercase tracking-[0.3em]" style={{ color }}>{cs.tag}</span>
          <h1 className="mt-4 max-w-4xl font-display text-5xl font-black leading-[0.95] tracking-tighter sm:text-6xl lg:text-7xl">
            {cs.client}
          </h1>
          <p className="mt-6 max-w-2xl text-lg font-light leading-relaxed text-muted">{cs.summary}</p>
        </div>
      </section>

      {/* Results strip */}
      <section className="border-y border-white/8 bg-surface/40">
        <div className="mx-auto grid max-w-7xl grid-cols-2 gap-px px-5 lg:grid-cols-4 lg:px-8">
          {cs.results.map((r) => (
            <div key={r.k} className="py-8 pr-6" data-testid={`cs-result-${r.k}`}>
              <div className="font-display text-3xl font-black lg:text-4xl" style={{ color }}>{r.v}</div>
              <div className="mt-2 text-sm font-medium text-white">{r.k}</div>
              <div className="text-xs text-muted">{r.note}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Context */}
      <section className="py-20 lg:py-28">
        <div className="mx-auto grid max-w-7xl grid-cols-1 gap-10 px-5 lg:grid-cols-12 lg:px-8">
          <Reveal className="lg:col-span-3">
            <p className="font-mono text-xs uppercase tracking-[0.3em]" style={{ color }}>The Brief</p>
          </Reveal>
          <Reveal className="lg:col-span-9" delay={0.1}>
            <p className="max-w-3xl font-display text-2xl font-light leading-relaxed text-white/90 lg:text-3xl">{cs.context}</p>
          </Reveal>
        </div>
      </section>

      {/* Timeline */}
      <section className="border-t border-white/8 py-20 lg:py-28" data-testid="cs-timeline">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <Reveal>
            <p className="mb-3 font-mono text-xs uppercase tracking-[0.3em]" style={{ color }}>Campaign Timeline</p>
            <h2 className="font-display text-4xl font-extrabold tracking-tight sm:text-5xl">How the growth happened.</h2>
          </Reveal>
          <div className="mt-14 space-y-0">
            {cs.timeline.map((t, i) => (
              <Reveal key={t.no} delay={i * 0.05}>
                <div className="group grid grid-cols-1 gap-4 border-t border-white/8 py-8 lg:grid-cols-12">
                  <div className="lg:col-span-2">
                    <span className="font-display text-5xl font-black" style={{ color, WebkitTextStroke: "0px" }}>{t.no}</span>
                  </div>
                  <h3 className="font-display text-2xl font-bold lg:col-span-3">{t.title}</h3>
                  <p className="max-w-xl text-base font-light leading-relaxed text-muted lg:col-span-7">{t.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Results / charts */}
      <section className="border-t border-white/8 py-20 lg:py-28" data-testid="cs-charts">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <Reveal>
            <p className="mb-3 font-mono text-xs uppercase tracking-[0.3em]" style={{ color }}>The Numbers</p>
            <h2 className="font-display text-4xl font-extrabold tracking-tight sm:text-5xl">
              {cs.chart?.type === "beforeAfter" ? "Before vs. after." : "Outcomes at a glance."}
            </h2>
          </Reveal>
          <div className="mt-12 grid grid-cols-1 gap-10 lg:grid-cols-12">
            <div className="lg:col-span-8">
              <Reveal>
                {cs.chart?.type === "beforeAfter" && <BeforeAfter metrics={cs.chart.metrics} color={color} />}
                {cs.chart?.type === "bars" && <BarsChart chart={cs.chart} color={color} />}
                {!cs.chart && (
                  <div className="grid grid-cols-2 gap-4">
                    {cs.results.map((r) => (
                      <div key={r.k} className="rounded-2xl border border-white/8 bg-surface p-6">
                        <div className="font-display text-2xl font-black" style={{ color }}>{r.v}</div>
                        <div className="mt-1 text-sm text-white">{r.k}</div>
                        <div className="text-xs text-muted">{r.note}</div>
                      </div>
                    ))}
                  </div>
                )}
              </Reveal>
            </div>
            <div className="lg:col-span-4">
              <Reveal delay={0.1}>
                <h4 className="mb-4 font-mono text-xs uppercase tracking-widest text-muted">What we did</h4>
                <ul className="space-y-3">
                  {cs.approach.map((a) => (
                    <li key={a} className="flex items-start gap-3 text-sm text-white/85"><Check className="mt-0.5 h-4 w-4 shrink-0" style={{ color }} /> {a}</li>
                  ))}
                </ul>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      {/* Other case studies */}
      <section className="border-t border-white/8 py-20 lg:py-28">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <h2 className="mb-10 font-display text-3xl font-extrabold tracking-tight">More growth stories</h2>
          <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
            {others.map((c) => (
              <Link key={c.id} to={`/case-study/${c.id}`} data-testid={`cs-more-${c.id}`} className="group overflow-hidden rounded-2xl border border-white/8 bg-surface">
                <div className="relative h-40 overflow-hidden">
                  <img src={c.image} alt={c.client} className="h-full w-full object-cover opacity-70 transition-transform duration-700 group-hover:scale-110" />
                  <div className="absolute inset-0 bg-gradient-to-t from-surface to-transparent" />
                </div>
                <div className="flex items-center justify-between p-5">
                  <span className="font-display text-lg font-bold">{c.client}</span>
                  <ArrowRight className="h-4 w-4 text-muted transition-transform group-hover:translate-x-1" />
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
